import { Stack, Typography } from '@mui/joy';
import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField, IAutocompleteProps } from 'core/components';
import { NON_EMPTY, NOT_AVAILABLE } from 'core/constants';
import { NeutralChip, TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { InventoryHelper } from 'core/helpers';
import { IAutocompleteOption } from 'core/types';
import { CorporateLocationType } from 'corporate/enums';
import { corporateLocationService } from 'corporate/services';
import { ICorporateLocationAutocomplete } from 'corporate/types';
import { Color, Direction, Level } from 'theme/enums';

export interface ICorporateLocationAutocompleteProps extends IAutocompleteProps {
  isAlternateVersionEnabled: boolean;
  type?: CorporateLocationType;
  displayInventory?: boolean;
  wrapperStyle?: CSSProperties;
}

export const CorporateLocationAutocomplete = ({
  name,
  requestPayload,
  disableClearable = false,
  required = false,
  getLabel = defaultGetLabel,
  isAlternateVersionEnabled,
  displayInventory = false,
  type = undefined,
  wrapperStyle,
  ...rest
}: ICorporateLocationAutocompleteProps) => {
  const { t } = useTranslation();

  const defaultRenderOptions = (
    option: ICorporateLocationAutocomplete,
    _: (option: ICorporateLocationAutocomplete) => string,
    inputValue?: string
  ) => {
    const availability = InventoryHelper.getInventoryAvailability(
      option.inventoryQuantity,
      option.inventoryUom
    );
    // A real 0 is falsy but known, so the unknown case can only be told apart by the predicate.
    const hasKnownQuantity = InventoryHelper.isKnownQuantity(option.inventoryQuantity);

    return (
      <Stack spacing={0}>
        <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <TextHighlighter text={getLabel(option)} highlight={inputValue} />
          {option.vendorId && (
            <NeutralChip>
              <TextHighlighter
                level={Level.TITLE_SM}
                text={option.vendorId}
                highlight={inputValue}
              />
            </NeutralChip>
          )}
        </Stack>

        {displayInventory && (
          <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
            {!hasKnownQuantity
              ? availability.value
              : availability.uom
              ? t('labelValueUomAvailable', availability)
              : t('labelValueAvailable', { value: availability.value })}
          </Typography>
        )}
      </Stack>
    );
  };

  const customRequestPayload = useMemo(() => {
    const filters: Record<string, unknown> = { ...(requestPayload?.filters || {}) };

    if (isAlternateVersionEnabled) {
      filters.vendorId = NON_EMPTY;
    }

    if (type) {
      if (isAlternateVersionEnabled) {
        switch (type) {
          case CorporateLocationType.SALES:
            filters.isAlternateSales = 1;
            break;
          case CorporateLocationType.SHIPPING:
            filters.isAlternateShipping = 1;
            break;
        }
      } else {
        switch (type) {
          case CorporateLocationType.SALES:
            filters.isSales = 1;
            break;
          case CorporateLocationType.SHIPPING:
            filters.isShipping = 1;
            break;
        }
      }
    }

    return {
      ...requestPayload,
      filters,
      viewBy: {
        ...((requestPayload?.viewBy as Record<string, unknown>) || {}),
        alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
      },
    };
  }, [requestPayload, isAlternateVersionEnabled, type]);

  return (
    <FormField
      name={name}
      type={InputType.AUTOCOMPLETE}
      inputProps={{
        getOptions: corporateLocationService.getLocationsAutocomplete,
        renderOptions: defaultRenderOptions,
        getLabel,
        disableClearable,
        required,
        requestPayload: customRequestPayload,
        listboxStyle: { gap: 0 },
        ...rest,
      }}
      wrapperStyle={wrapperStyle}
    />
  );
};

const defaultGetLabel = (option: IAutocompleteOption): string => {
  const item = option as unknown as ICorporateLocationAutocomplete;

  return item.name || NOT_AVAILABLE;
};
